import {ContentTile, ContentTileEmptyText} from '#/atoms/content-tile';
import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {TileFilterButton} from '#/components/buttons/filter-button';
import {IconButton} from '#/components/buttons/icon-button';
import {ProfileLinkTransactionTile} from '#/components/transaction/profile-link-transaction-tile';
import {
  transactionActivityTileProps,
  transactionTypeConstants,
} from '#/components/transaction/transaction-type-constants';
import {useScrollEdges} from '#/hooks/use-is-scrolling';
import {useObjectQueueSet} from '#/hooks/use-queue-set';
import {useRefSrollIntoView} from '#/hooks/use-ref-scroll-into-view';
import {useLandTransactions} from '#/hooks/use-transactions';
import {ArrowUpIcon} from '@heroicons/react/24/outline';
import {FunctionComponent, useContext} from 'react';
import {useTranslation} from 'react-i18next';
import {TransactionType, displayableTransactionTypes} from 'shareland-common';

export const ActionMenuContentSectionRecentActivity: FunctionComponent = () => {
  const {
    values: selectedFilters,
    open: setFilter,
    close: removeFilter,
    clear: clearFilters,
  } = useObjectQueueSet<TransactionType>();

  return (
    <>
      <FilterByTransactionTypeContentTile
        selectedFilters={selectedFilters}
        setFilter={setFilter}
        removeFilter={removeFilter}
        clearFilters={clearFilters}
      />

      <ActivityTransactions selectedFilters={selectedFilters} />
    </>
  );
};

const ActivityTransactions: FunctionComponent<{
  selectedFilters: TransactionType[];
}> = ({selectedFilters}) => {
  const {land} = useContext(ActionMenuContext);

  const {t} = useTranslation('translation');

  const [topRef, topScrollIntoView] = useRefSrollIntoView<HTMLDivElement>(100);
  const [buttonRef, buttonScrollIntoView] = useRefSrollIntoView<HTMLButtonElement>(100);

  const {
    data: baseTransactions = [],
    isApiLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useLandTransactions(land, 10);

  const transactions = !!selectedFilters.length
    ? baseTransactions.filter(({type}) => selectedFilters.includes(type))
    : baseTransactions;

  const isLoading = isApiLoading && !isFetchingNextPage;
  const isEmpty = !isLoading && transactions && transactions.length === 0;

  const onShowNextClick = async () => {
    await fetchNextPage();
    buttonScrollIntoView();
  };

  if (isLoading) {
    return (
      <div className='w-full h-16 py-4 rounded-xl text-gray-300 bg-graybluedark3 flex-center animate-pulse mb-4'>
        {t('statuses.loadingDots')}
      </div>
    );
  }

  if (isEmpty) {
    return (
      <ContentTile className='grow py-10 flex-center'>
        <ContentTileEmptyText>
          {selectedFilters.length === 0
            ? t('pages.actionMenu.information.sections.activity.empty')
            : t('pages.actionMenu.information.sections.activity.emptyWithFilters')}
        </ContentTileEmptyText>
      </ContentTile>
    );
  }

  return (
    <div className={'grow flex flex-col gap-3 relative'}>
      <div className='w-full h-0 absolute top-0' ref={topRef} />

      {transactions.map((transaction, index) => (
        <ProfileLinkTransactionTile key={index} land={land} transaction={transaction} />
      ))}

      <div className='flex gap-3 justify-end items-stretch'>
        {transactions && hasNextPage && (
          <button
            ref={buttonRef}
            className='grow h-14 py-4 rounded-xl text-white opacity-50 hover:opacity-100 border-2 font-semibold border-white bg-transparent flex-center'
            onClick={onShowNextClick}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? t('statuses.loadingDots') : t('actions.showMore')}
          </button>
        )}
        {transactions.length > 5 && (
          <IconButton
            className='h-14 w-14 p-3 rounded-full border-2 border-white flex-center opacity-50 hover:opacity-100'
            IconComponent={ArrowUpIcon}
            iconClassName={'stroke-white fill-white'}
            onClick={() => {
              topScrollIntoView();
            }}
          />
        )}
      </div>
    </div>
  );
};

export const FilterByTransactionTypeContentTile: FunctionComponent<{
  selectedFilters: TransactionType[];
  setFilter: (transactionType: TransactionType) => void;
  removeFilter: (transactionType: TransactionType) => void;
  clearFilters: () => void;
}> = ({selectedFilters, setFilter, removeFilter, clearFilters}) => {
  const {t} = useTranslation('translation');

  const {scrollEdges, onScroll} = useScrollEdges();

  return (
    <ContentTile className='overflow-y-visible space-y-2.5'>
      <div className='flex gap-2 justify-between items-center '>
        <span className='text-deepblue font-semibold text-sm uppercase'>
          {t('pages.actionMenu.information.sections.activity.ctaFilter')}
        </span>
        <button
          className={'text-white text-xs font-semibold gap-1 px-2 py-0.5 hover:opacity-70 disabled:opacity-30'}
          onClick={clearFilters}
          disabled={selectedFilters.length === 0}
        >
          {t('pages.actionMenu.information.sections.activity.ctaClearFilters')}
        </button>
      </div>
      <div className='relative flex justify-start items-stretch gap-3'>
        <div className='relative grow overflow-hidden'>
          <div onScroll={onScroll} className='flex gap-2 overflow-x-scroll'>
            {displayableTransactionTypes.map(transactionType => {
              const {i18nKey} = transactionTypeConstants[transactionType];
              const activityTileProps = transactionActivityTileProps[transactionType];

              return (
                <TileFilterButton<TransactionType>
                  key={transactionType}
                  filter={Number(transactionType)}
                  selectedFilters={selectedFilters}
                  setFilter={setFilter}
                  removeFilter={removeFilter}
                  title={t(`transactions.${i18nKey}.present`)}
                  IconComponent={activityTileProps!.IconComponent}
                  iconColorClassName={activityTileProps!.iconColorClassName}
                  primaryTextColor={activityTileProps!.primaryTextColor}
                  bgColor={activityTileProps!.bgColor}
                />
              );
            })}
          </div>

          {(scrollEdges === 'right' || scrollEdges === 'both') && (
            <div className='absolute top-0 left-0 h-full flex pointer-events-none'>
              <div className='h-full aspect-square bg-gradient-to-r from-graybluedark' />
            </div>
          )}
          {(scrollEdges === 'left' || scrollEdges === 'both') && (
            <div className='absolute top-0 right-0 h-full flex pointer-events-none'>
              <div className='h-full aspect-square bg-gradient-to-l from-graybluedark' />
            </div>
          )}
        </div>
      </div>
    </ContentTile>
  );
};
